import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { tFamily } from "@/i18n";
import { ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

/**
 * Fragrance Finder — 5-step quiz that recommends 6 perfumes based on
 * gender, olfactive family, occasion/season, intensity and budget.
 */

const STEPS = [
  {
    key: "gender",
    titleKey: "finder.step_gender",
    hintKey: "finder.hint_gender",
    options: [
      { value: "Women", labelKey: "finder.opt_women" },
      { value: "Men", labelKey: "finder.opt_men" },
      { value: "Unisex", labelKey: "finder.opt_unisex" },
    ],
    multi: false,
  },
  {
    key: "families",
    titleKey: "finder.step_families",
    hintKey: "finder.hint_families",
    options: [
      { value: "floral", familyKey: "floral", copyEs: "Rosa, jazmín, peonía", copyEn: "Rose, jasmine, peony" },
      { value: "amaderado", familyKey: "amaderado", copyEs: "Cedro, sándalo, vetiver", copyEn: "Cedar, sandalwood, vetiver" },
      { value: "citrico", familyKey: "citrico", copyEs: "Bergamota, limón, mandarina", copyEn: "Bergamot, lemon, mandarin" },
      { value: "oriental", familyKey: "oriental", copyEs: "Ámbar, oud, especias", copyEn: "Amber, oud, spice" },
      { value: "gourmand", familyKey: "gourmand", copyEs: "Vainilla, caramelo, café", copyEn: "Vanilla, caramel, coffee" },
    ],
    multi: true,
  },
  {
    key: "season",
    titleKey: "finder.step_season",
    hintKey: "finder.hint_season",
    options: [
      { value: "primavera", labelKey: "finder.opt_spring" },
      { value: "verano", labelKey: "finder.opt_summer" },
      { value: "otono", labelKey: "finder.opt_autumn" },
      { value: "invierno", labelKey: "finder.opt_winter" },
    ],
    multi: false,
  },
  {
    key: "intensity",
    titleKey: "finder.step_intensity",
    hintKey: "finder.hint_intensity",
    options: [
      { value: "ligero", labelKey: "finder.opt_light", copyKey: "finder.opt_light_copy" },
      { value: "moderado", labelKey: "finder.opt_moderate", copyKey: "finder.opt_moderate_copy" },
      { value: "intenso", labelKey: "finder.opt_intense", copyKey: "finder.opt_intense_copy" },
    ],
    multi: false,
  },
  {
    key: "budget",
    titleKey: "finder.step_budget",
    options: [
      { value: "eco", labelKey: "finder.opt_budget_eco", copyKey: "finder.opt_budget_eco_copy" },
      { value: "medio", labelKey: "finder.opt_budget_medium", copyKey: "finder.opt_budget_medium_copy" },
      { value: "premium", labelKey: "finder.opt_budget_premium", copyKey: "finder.opt_budget_premium_copy" },
    ],
    multi: false,
  },
];

export default function Finder() {
  const { t, i18n } = useTranslation();
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState({ families: [] });
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);

  const optLabel = (opt) => {
    if (opt.labelKey) return t(opt.labelKey);
    if (opt.familyKey) return tFamily(opt.familyKey);
    return opt.value;
  };
  const optCopy = (opt) => {
    if (opt.copyKey) return t(opt.copyKey);
    if (opt.familyKey) return i18n.language === "en" ? opt.copyEn : opt.copyEs;
    return null;
  };

  const current = STEPS[step];
  const selected = current && (current.multi ? answers[current.key] || [] : answers[current.key]);
  const canNext = current && (current.multi ? (answers[current.key] || []).length > 0 : answers[current.key]);
  const isLast = step === STEPS.length - 1;

  const pick = (val) => {
    if (current.multi) {
      const arr = answers[current.key] || [];
      const next = arr.includes(val) ? arr.filter((x) => x !== val) : [...arr, val];
      setAnswers({ ...answers, [current.key]: next });
    } else {
      setAnswers({ ...answers, [current.key]: val });
    }
  };

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        gender: answers.gender,
        families: answers.families || [],
        season: answers.season,
        intensity: answers.intensity,
        budget: answers.budget,
      };
      const { data } = await api.post("/finder/recommend", payload);
      setResults(data);
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setResults(null);
    setAnswers({ families: [] });
    setStep(0);
  };

  // Results view
  if (results) {
    return (
      <div className="min-h-[80vh] bg-cream">
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 md:py-24">
          <div className="mb-12 fade-up">
            <div className="font-mono-caps text-royal flex items-center gap-2">
              <Sparkles strokeWidth={1.25} className="h-4 w-4" /> {t("finder.profile_eyebrow")}
            </div>
            <h1 className="font-serif text-4xl md:text-6xl mt-3 leading-tight">
              {t("finder.recos_title")}
            </h1>
            <p className="text-muted-foreground mt-4 max-w-xl">
              {t("finder.recos_copy")}
            </p>
          </div>

          {results.length === 0 ? (
            <div className="text-center py-24">
              <p className="font-serif text-2xl mb-6">{t("finder.no_match")}</p>
              <button onClick={restart} className="border border-navy px-6 py-3 font-mono-caps hover:bg-navy hover:text-cream transition-colors">
                {t("finder.retry")}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-8" data-testid="finder-results">
              {results.map((p, i) => {
                const price = Math.min(...p.sizes.map((s) => s.price));
                return (
                  <Link
                    key={p.id}
                    to={`/producto/${p.slug}`}
                    className="group block fade-up"
                    style={{ animationDelay: `${i * 80}ms` }}
                    data-testid={`finder-reco-${p.slug}`}
                  >
                    <div className="relative bg-white aspect-square mb-4 overflow-hidden">
                      <span className="absolute top-3 left-3 font-mono-caps bg-navy text-cream text-[10px] px-2 py-1">
                        Match #{i + 1}
                      </span>
                      <img src={p.images[0]} alt={p.name} loading="lazy" className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105" />
                    </div>
                    <div className="font-mono-caps text-muted-foreground">{p.brand}</div>
                    <h3 className="font-serif text-xl leading-tight">{p.name}</h3>
                    <div className="flex items-baseline justify-between mt-1">
                      <div className="text-sm text-muted-foreground">{p.concentration} · {tFamily(p.family)}</div>
                      <div className="font-medium">{i18n.language === "en" ? "From" : "Desde"} ${price.toFixed(2)}</div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )}

          <div className="mt-16 flex flex-wrap gap-4">
            <button
              onClick={restart}
              className="border border-navy px-6 py-4 font-mono-caps hover:bg-navy hover:text-cream transition-colors"
              data-testid="finder-restart"
            >
              {t("finder.restart")}
            </button>
            <Link to="/shop" className="bg-navy text-cream px-6 py-4 font-mono-caps hover:bg-cream hover:text-navy border border-navy transition-colors">
              {t("finder.see_all")}
            </Link>
          </div>
        </section>
      </div>
    );
  }

  // Quiz view
  return (
    <div className="min-h-[80vh] bg-cream">
      <section className="max-w-3xl mx-auto px-6 lg:px-12 py-16 md:py-24">
        <div className="mb-10">
          <div className="flex items-center gap-2 font-mono-caps text-royal text-xs">
            <Sparkles strokeWidth={1.25} className="h-4 w-4" /> {t("finder.title")}
          </div>
          <div className="flex items-center gap-2 mt-6" aria-hidden>
            {STEPS.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 transition-colors ${i <= step ? "bg-navy" : "bg-border"}`}
              />
            ))}
          </div>
          <div className="font-mono-caps text-muted-foreground mt-3 text-xs">
            {t("finder.step_of", { current: step + 1, total: STEPS.length })}
          </div>
        </div>

        <h1 className="font-serif text-4xl md:text-5xl leading-tight" data-testid={`finder-step-${current.key}`}>
          {t(current.titleKey)}
        </h1>
        {current.hintKey && (
          <p className="text-muted-foreground mt-3">{t(current.hintKey)}</p>
        )}

        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {current.options.map((opt) => {
            const isSelected = current.multi ? (answers[current.key] || []).includes(opt.value) : answers[current.key] === opt.value;
            const copy = optCopy(opt);
            return (
              <button
                key={opt.value}
                onClick={() => pick(opt.value)}
                className={`text-left p-6 border transition-all ${isSelected ? "border-navy bg-navy text-cream" : "border-border hover:border-navy"}`}
                data-testid={`finder-opt-${current.key}-${opt.value}`}
              >
                <div className={`font-serif text-2xl ${isSelected ? "text-cream" : ""}`}>{optLabel(opt)}</div>
                {copy && (
                  <div className={`mt-2 text-sm ${isSelected ? "text-cream/70" : "text-muted-foreground"}`}>
                    {copy}
                  </div>
                )}
              </button>
            );
          })}
        </div>

        <div className="mt-12 flex items-center justify-between">
          <button
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            disabled={step === 0}
            className="inline-flex items-center gap-2 font-mono-caps disabled:opacity-30 hover:text-royal"
            data-testid="finder-prev"
          >
            <ArrowLeft strokeWidth={1.25} className="h-4 w-4" /> {t("finder.prev")}
          </button>
          {isLast ? (
            <button
              onClick={submit}
              disabled={!canNext || loading}
              className="inline-flex items-center gap-3 bg-navy text-cream px-8 py-4 font-mono-caps hover:bg-cream hover:text-navy border border-navy transition-colors disabled:opacity-40"
              data-testid="finder-submit"
            >
              {loading ? t("finder.loading") : t("finder.submit")}
              <ArrowRight strokeWidth={1.25} className="h-4 w-4" />
            </button>
          ) : (
            <button
              onClick={() => setStep((s) => s + 1)}
              disabled={!canNext}
              className="inline-flex items-center gap-3 bg-navy text-cream px-8 py-4 font-mono-caps hover:bg-cream hover:text-navy border border-navy transition-colors disabled:opacity-40"
              data-testid="finder-next"
            >
              {t("finder.next")} <ArrowRight strokeWidth={1.25} className="h-4 w-4" />
            </button>
          )}
        </div>
      </section>
    </div>
  );
}
